import * as React from 'react';
import classNames from 'classnames';
import { assign as _assign, difference as _difference, includes as _includes } from 'lodash';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { LEFT_RIGHT, SOAP_DISPENSER_TYPE, ConfigurationModel, BuiltInPartsModel, BuiltInPartsDefaultModel, ConfigurationDefaultModel } from 'franke-trough-dependencies';
import { requestAPIActions } from 'loopback-react-request';
import { ElementPositions } from '../Utils';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './Recesses.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';

export interface SoapDispenserHoleProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: any;
}

export interface SoapDispenserHoleState {
  soapDispenserHole: SOAP_DISPENSER_TYPE;
  soapDispenserHolePositions: null | LEFT_RIGHT[];
}

const SoapDispenserHoleLabel: React.SFC<{value: SOAP_DISPENSER_TYPE}> = (props) => {
  switch (props.value) {
    case 'without':
      return (<span><FormattedMessage {...MESSAGES.soapDispenserHoleLabelWithout} /></span>);
    case 'SD80':
      return (<span><FormattedMessage {...MESSAGES.soapDispenserHoleLabelSD80} /></span>);
    case 'SD100':
      return (<span><FormattedMessage {...MESSAGES.soapDispenserHoleLabelSD100} /></span>);
    case 'others':
      return (<span><FormattedMessage {...MESSAGES.soapDispenserHoleLabel25} /></span>);
    default:
      return null;
  }  
};

export class SoapDispenserHolePartial extends React.Component<SoapDispenserHoleProps, SoapDispenserHoleState> {
  constructor(props: SoapDispenserHoleProps) {
    super(props);
    this.state = {
      soapDispenserHole: 'without',
      soapDispenserHolePositions: null,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handlePositionChange = this.handlePositionChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      soapDispenserHole: this.soapDispenserHole,
      soapDispenserHolePositions: this.soapDispenserHolePositions || [],
    });
  }

  componentDidUpdate(prevProps: SoapDispenserHoleProps) {
    const configuration = this.configuration;
    const prevConfiguration = prevProps.configuration && prevProps.configuration.result;
    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      if (this.soapDispenserHole !== this.state.soapDispenserHole) {
        this.setState({
          soapDispenserHole: this.soapDispenserHole,
        });
      }
      if (this.soapDispenserHolePositions && JSON.stringify(this.soapDispenserHolePositions) !== JSON.stringify(this.state.soapDispenserHolePositions)) {
        this.setState({
          soapDispenserHolePositions: this.soapDispenserHolePositions,
        });
      }
    }
  }

  public render() {
    const { soapDispenserHole, soapDispenserHolePositions } = this.state;
    return (
      <div className="partial-soap-dispenser-hole position-relative pb-4">
        {this.soapDispenserHoleDefaults && this.soapDispenserHoleDefaults.allValues && this.soapDispenserHoleDefaults.allValues.map((value, index) => {
          const key = `has_soapDispenserHole_${index}`;
          const disabled = !(_includes(this.soapDispenserHoleDefaults && this.soapDispenserHoleDefaults.values, value))

          return (
            <div key={key} className="col-12">
              <label className={classNames({disabled: this.disabled || disabled})}>
                <SoapDispenserHoleLabel value={value} />
                <input 
                  checked={soapDispenserHole === value ? true : false}
                  disabled={this.disabled || disabled}
                  onChange={this.handleChange}
                  name="soapDispenserHole"
                  type="radio"
                  value={value}
                />
                <span className="checkmark" />
              </label>
              {soapDispenserHole !== 'without' && soapDispenserHole === value && soapDispenserHolePositions && this.positionDefaults && (
                <ElementPositions
                  disabled={this.disabled || disabled}
                  defaults={this.positionDefaults}
                  id={key}
                  positionChange={this.handlePositionChange}
                  positions={soapDispenserHolePositions}
                  selectedTrough={this.selectedTrough}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }
  
  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration && this.props.configuration.result;
  }

  private get defaults(): ConfigurationDefaultModel | undefined {
    return (this.configuration && this.configuration.defaults);
  }

  private get disabled(): boolean {
    return !isConfigurationEditable();
  }

  private dispatchConfiguration(props: SoapDispenserHoleState) {
    if (!this.configuration || !this.configuration.builtInParts) {
      return;
    }
    const builtInParts: BuiltInPartsModel = this.configuration.builtInParts as BuiltInPartsModel;
    const soapDispenserPositions = props.soapDispenserHolePositions;
    let tapHolePosition = builtInParts.tapHolePositions;

    if (soapDispenserPositions) {
      Object.keys(soapDispenserPositions).forEach(key => {
        if (tapHolePosition && tapHolePosition[key] && soapDispenserPositions && soapDispenserPositions[key]) {        
          tapHolePosition[key] = soapDispenserPositions[key] === 'left'  ? 'right' : 'left';
        }
      });
    }

    const data: BuiltInPartsModel = _assign({}, builtInParts, {
      soapDispenserHole: props.soapDispenserHole,
      soapDispenserHolePositions: props.soapDispenserHolePositions || null,
      tapHolePositions: tapHolePosition
    });
    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {builtInParts: data}
        },
      },
      true,
    ));
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const soapDispenserHole = target.value as SOAP_DISPENSER_TYPE;
    const state = {
      soapDispenserHole,
      soapDispenserHolePositions: soapDispenserHole !== 'without' ? this.state.soapDispenserHolePositions : null,
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private handlePositionChange(positions: LEFT_RIGHT[]) {
    const soapDispenserHolePositions = positions;
    const state = {
      soapDispenserHole: this.state.soapDispenserHole,
      soapDispenserHolePositions
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }
  private get positionDefaults(): BuiltInPartsDefaultModel['soapDispenserHolePositions'] | null | undefined {
    return this.defaults && this.defaults.builtInParts && this.defaults.builtInParts.soapDispenserHolePositions;
  }
  private get soapDispenserHole(): BuiltInPartsModel['soapDispenserHole'] {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.soapDispenserHole ? this.configuration.builtInParts.soapDispenserHole : 'without';
  }

  private get soapDispenserHolePositions(): BuiltInPartsModel['soapDispenserHolePositions'] | null | undefined {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.soapDispenserHolePositions;
  }

  private get soapDispenserHoleDefaults(): BuiltInPartsDefaultModel['soapDispenserHole'] | undefined {
    return this.defaults && this.defaults.builtInParts &&
      this.defaults.builtInParts.soapDispenserHole;
  }

  private get selectedTrough(): ConfigurationModel['selectedTrough'] | undefined {
    return this.configuration &&
      this.configuration.selectedTrough;
  }
}

export const SoapDispenserHole = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration
}))(SoapDispenserHolePartial);

export default SoapDispenserHole;